/**
 * Placement déterministe du décor de village (petits chalets) et des jalons
 * de piste. Calcul pur, sans Three.js : le décor l'appelle à chaque
 * reconstruction, et il se mesure hors navigateur. Les tirages aléatoires sont
 * faits pour chaque case de la grille avant tout test d'exclusion : construire
 * un bâtiment retire les chalets qui le gênent sans déplacer les autres.
 */
import {
  BUILDINGS,
  Rng,
  buildingRadius,
  distPointSegment,
  type BuildingKind,
  type GameMap,
  type Heightfield,
  type Vec2,
} from '@snowchain/shared';

export interface DecorItem {
  name: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  s: number;
}

export interface PoleItem extends DecorItem {
  difficulty: string;
}

interface LiftLike {
  bottom: Vec2;
  top: Vec2;
}
interface BuildingLike {
  kind: BuildingKind;
  x: number;
  z: number;
}
interface PisteLike {
  path: Vec2[];
  width: number;
  difficulty: string;
}

/** Pas de la grille des chalets (m). */
const HOUSE_STEP = 26;
/** Probabilité d'un chalet au bord intérieur de l'anneau, décroissante vers l'extérieur. */
const HOUSE_DENSITY = 0.45;
/** Dégagement autour des couloirs de marche des visiteurs (m). */
const CORRIDOR_CLEARANCE = 30;
/** Portée des couloirs : gares aval à moins de cette distance d'un point d'arrivée (m). */
const CORRIDOR_REACH = 450;

/**
 * Couloirs de circulation : segments que les visiteurs parcourent à pied en
 * ligne droite, de chaque zone d'accueil (ou chalet d'accueil) vers chaque
 * gare aval proche, et vers les comptoirs (caisses, location).
 */
export function walkCorridors(lifts: Iterable<LiftLike>, buildings: Iterable<BuildingLike>): [Vec2, Vec2][] {
  const blds = [...buildings];
  const exits: Vec2[] = [];
  for (const b of blds) if (b.kind === 'welcome_area') exits.push([b.x, b.z]);
  if (!exits.length) for (const b of blds) if (b.kind === 'chalet_accueil') exits.push([b.x, b.z + 14]);
  const targets: Vec2[] = [];
  for (const l of lifts) targets.push(l.bottom);
  for (const b of blds) {
    const s = BUILDINGS[b.kind];
    if (s.ticketsPerHour || s.rentalsPerHour || s.lessonsPerDay) targets.push([b.x, b.z]);
  }
  const out: [Vec2, Vec2][] = [];
  for (const e of exits) for (const t of targets) if (Math.hypot(t[0] - e[0], t[1] - e[1]) < CORRIDOR_REACH) out.push([e, t]);
  return out;
}

/** Direction de la descente (opposé du gradient du relief), en angle de lacet (+z local vers l'aval). */
function downhillYaw(hf: Heightfield, x: number, z: number): number {
  const e = 4;
  const gx = hf.height(x + e, z) - hf.height(x - e, z);
  const gz = hf.height(x, z + e) - hf.height(x, z - e);
  if (Math.abs(gx) + Math.abs(gz) < 1e-6) return 0;
  return Math.atan2(-gx, -gz);
}

/**
 * Petits chalets du village : grille bruitée dans l'anneau entre 0,55 x le
 * rayon du village et la zone constructible, densité décroissante vers
 * l'extérieur, hors pistes, remontées, bâtiments, lac et couloirs de marche.
 * `onPiste(x, z)` dit si le point est sur une piste ou sa marge.
 */
export function placeVillageHouses(
  hf: Heightfield,
  map: GameMap,
  onPiste: (x: number, z: number) => boolean,
  lifts: Iterable<LiftLike>,
  buildings: Iterable<BuildingLike>,
): DecorItem[] {
  const v = map.features.village;
  const lake = map.features.lake;
  const inner = v.radius * 0.55;
  const outer = map.villageZone;
  const liftList = [...lifts];
  const liftSegs = liftList.map((l) => [l.bottom, l.top] as [Vec2, Vec2]);
  const bldList = [...buildings];
  const blds = bldList.map((b) => ({ x: b.x, z: b.z, r: buildingRadius(b.kind) + 12 }));
  const corridors = walkCorridors(liftList, bldList);
  const rng = new Rng(map.features.seed * 53 + 11);
  const out: DecorItem[] = [];
  for (let gz = v.z - outer; gz <= v.z + outer; gz += HOUSE_STEP) {
    for (let gx = v.x - outer; gx <= v.x + outer; gx += HOUSE_STEP) {
      // Tirages faits avant toute exclusion (stabilité des placements).
      const x = gx + rng.range(-6, 6);
      const z = gz + rng.range(-6, 6);
      const r = rng.next();
      const variant = rng.next();
      const sc = rng.range(0.9, 1.1);
      const jitter = rng.range(-0.15, 0.15);
      const dv = Math.hypot(x - v.x, z - v.z);
      if (dv < inner || dv > outer) continue;
      const t = (dv - inner) / (outer - inner);
      if (r > HOUSE_DENSITY * (1 - t) ** 1.6) continue;
      if (!hf.inBounds(x, z, 40)) continue;
      if (hf.slope(x, z) >= 0.3) continue;
      if (Math.hypot(x - lake.x, z - lake.z) < lake.radius + 15) continue;
      if (onPiste(x, z)) continue;
      if (liftSegs.some(([a, b]) => distPointSegment([x, z], a, b) < 22)) continue;
      if (blds.some((b) => Math.hypot(x - b.x, z - b.z) < b.r)) continue;
      if (corridors.some(([a, b]) => distPointSegment([x, z], a, b) < CORRIDOR_CLEARANCE)) continue;
      out.push({ name: variant < 0.5 ? 'chalet_small_a' : 'chalet_small_b', x, y: hf.height(x, z) - 0.3, z, yaw: downhillYaw(hf, x, z) + jitter, s: sc });
    }
  }
  return out;
}

/** Espacement des jalons le long d'une piste (m). */
const POLE_STEP = 32;

/**
 * Jalons de piste sur les deux bords (largeur/2 + 0,6 m), tous les 32 m,
 * sauf près du départ et de l'arrivée (15 m) et sous les remontées (8 m).
 */
export function placePistePoles(hf: Heightfield, pistes: Iterable<PisteLike>, lifts: Iterable<LiftLike>): PoleItem[] {
  const liftSegs = [...lifts].map((l) => [l.bottom, l.top] as [Vec2, Vec2]);
  const out: PoleItem[] = [];
  for (const p of pistes) {
    let total = 0;
    for (let i = 0; i < p.path.length - 1; i++) {
      const a = p.path[i] as Vec2;
      const b = p.path[i + 1] as Vec2;
      total += Math.hypot(b[0] - a[0], b[1] - a[1]);
    }
    const off = p.width / 2 + 0.6;
    let acc = 0;
    let next = POLE_STEP / 2;
    for (let i = 0; i < p.path.length - 1; i++) {
      const a = p.path[i] as Vec2;
      const b = p.path[i + 1] as Vec2;
      const seg = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (seg < 1e-6) continue;
      const dx = (b[0] - a[0]) / seg;
      const dz = (b[1] - a[1]) / seg;
      while (next <= acc + seg) {
        const along = next;
        next += POLE_STEP;
        if (along < 15 || along > total - 15) continue;
        const k = (along - acc) / seg;
        const cx = a[0] + (b[0] - a[0]) * k;
        const cz = a[1] + (b[1] - a[1]) * k;
        for (const side of [1, -1]) {
          const x = cx + dz * off * side;
          const z = cz - dx * off * side;
          if (liftSegs.some(([la, lb]) => distPointSegment([x, z], la, lb) < 8)) continue;
          out.push({ name: 'piste_pole', x, y: hf.height(x, z) - 0.1, z, yaw: Math.atan2(dx, dz), s: 1, difficulty: p.difficulty });
        }
      }
      acc += seg;
    }
  }
  return out;
}
